import React from 'react';
import { format } from 'date-fns';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ApiKeyResponseDto, ApiKeyStatus } from '../common/model/dto/api-key';
import { StatusTone } from '../common/model/type/status-tone.type';
import { RecordColumn } from '../common/model/ui/record-list.type';
import OvPillLink from '../components/UI/atoms/OvPillLink';
import OvStatusChip from '../components/UI/atoms/OvStatusChip';
import OvApiKeyRowActions from '../components/UI/molecules/OvApiKeyRowActions';

interface ApiKeyRowHandlers {
  getNavigationState: () => unknown;
  onDelete: (id: string) => void;
  onRevoke: (id: string) => void;
  onActivate: (id: string) => void;
}

export const API_KEY_STATUS_TONES: Record<ApiKeyStatus, StatusTone> = {
  [ApiKeyStatus.ACTIVE]: 'success',
  [ApiKeyStatus.INACTIVE]: 'neutral',
  [ApiKeyStatus.REVOKED]: 'failure',
};

export function useApiKeyListColumns({
  getNavigationState,
  onDelete,
  onRevoke,
  onActivate,
}: ApiKeyRowHandlers): RecordColumn<ApiKeyResponseDto>[] {
  const { t } = useTranslation();

  const renderStatus = (apiKey: ApiKeyResponseDto) => {
    const statusChip = (
      <span>
        <OvStatusChip
          label={t(`apiKeys.status.${apiKey.status}`)}
          tone={API_KEY_STATUS_TONES[apiKey.status]}
        />
      </span>
    );

    return apiKey.status === ApiKeyStatus.REVOKED &&
      apiKey.revocation_reason ? (
      <Tooltip
        title={`${t('apiKeys.revocationReason')}: ${apiKey.revocation_reason}`}
      >
        {statusChip}
      </Tooltip>
    ) : (
      statusChip
    );
  };

  return [
    {
      id: 'partner-name',
      header: t('apiKeys.fields.partnerName'),
      sortKey: 'partner_name',
      cardRole: 'heading',
      renderCell: (apiKey) => apiKey.partner_name,
    },
    {
      id: 'partner-route',
      header: t('apiKeys.fields.partnerRoute'),
      sortKey: 'partner_route',
      renderCell: (apiKey) => (
        <PartnerRoute>{apiKey.partner_route}</PartnerRoute>
      ),
    },
    {
      id: 'status',
      header: t('apiKeys.fields.status'),
      sortKey: 'status',
      renderCell: renderStatus,
    },
    {
      id: 'usage-count',
      header: t('apiKeys.fields.usageCount'),
      sortKey: 'usage_count',
      renderCell: (apiKey) => apiKey.usage_count,
    },
    {
      id: 'created-at',
      header: t('apiKeys.fields.createdAt'),
      sortKey: 'created_at',
      renderCell: (apiKey) =>
        apiKey.created_at
          ? format(new Date(apiKey.created_at), 'MMM dd, yyyy')
          : '-',
    },
    {
      id: 'operation',
      header: t('common.actions.title'),
      isHeaderHidden: true,
      renderCell: (apiKey) => (
        <OvApiKeyRowActions
          apiKey={apiKey}
          onDelete={onDelete}
          onRevoke={onRevoke}
          onActivate={onActivate}
        />
      ),
    },
    {
      id: 'action',
      header: t('common.action'),
      isHeaderHidden: true,
      cardRole: 'footer',
      renderCell: (apiKey, detailsPath = '') => (
        <OvPillLink
          to={detailsPath}
          state={getNavigationState()}
          aria-label={t('apiKeys.detailsFor', { name: apiKey.partner_name })}
        >
          {t('common.actions.seeDetails')}
        </OvPillLink>
      ),
    },
  ];
}

const PartnerRoute = styled.div`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
